import { useMemo } from 'react';
import cx from 'classnames';

import { HuzzleUserAvatar } from 'components/HuzzleUserAvatar';
import Link from 'components/Link';
import DatePosted from 'components/DatePosted';
import { parseResponse } from 'lib/utils/parser';
import { TStudentSociety } from 'lib/models/student-society';
import { TCompany } from 'lib/models/company';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { UserAvatarFields } from 'lib/models/User.model';
import { PAGE_ROUTES } from 'lib/page-routes';
import { getUserAvatarFields, isCompanyResponse } from 'utils/user';
import { getOpportunityAcceptedCohosts } from 'utils/cohost';

import styles from './PostedByInfo.module.scss';

type PostedByInfoProps = {
  className?: string;
};

export const PostedByInfo: React.FC<PostedByInfoProps> = ({ className }) => {
  const { opportunity } = useOpportunity();
  const { creatable_for: creatableForResponse, posted_at, cohost_applications: cohostApplicationsResponse } = opportunity;
  const { name, companyAvatar, societyAvatar, slug, publicURLForSingleHost } = useMemo(() => {
    if (isCompanyResponse(creatableForResponse)) {
      const createdBy = parseResponse<TCompany>(creatableForResponse);
      const companyAvatar = getUserAvatarFields({ company: createdBy }) as UserAvatarFields;
      return {
        name: createdBy.name,
        slug: createdBy.slug,
        companyAvatar,
        publicURLForSingleHost: PAGE_ROUTES.COMPANY_PUBLIC.replace('[slug]', createdBy.slug),
      };
    } else {
      const createdBy = parseResponse<TStudentSociety>(creatableForResponse);
      const societyAvatar = getUserAvatarFields({ society: createdBy }) as UserAvatarFields;
      return {
        name: createdBy.name,
        slug: createdBy.slug,
        societyAvatar,
        publicURLForSingleHost: PAGE_ROUTES.SOCIETY_PUBLIC.replace('[slug]', createdBy.slug),
      };
    }
  }, [creatableForResponse]);

  const cohosts = useMemo(() => {
    if (!societyAvatar) return [];
    const cohosts = getOpportunityAcceptedCohosts(cohostApplicationsResponse).map((cohost) => ({
      societyAvatar: getUserAvatarFields({ society: cohost }) as UserAvatarFields,
      name: cohost.name,
      slug: cohost.slug,
    }));
    return [{ name, societyAvatar, slug }, ...cohosts];
  }, [cohostApplicationsResponse, name, societyAvatar]);

  return (
    <div className={cx(styles.container, className)}>
      {companyAvatar && <HuzzleUserAvatar url={publicURLForSingleHost} user={companyAvatar} size="xsmall" tickSize="xsmall" />}
      {cohosts.length > 0 && (
        <div className={styles.cohosts} style={{ width: `${cohosts.length * 24}px` }}>
          {cohosts.map(({ name, societyAvatar, slug }, index) => {
            const publicURL = PAGE_ROUTES.SOCIETY_PUBLIC.replace('[slug]', slug);

            return (
              <HuzzleUserAvatar
                url={publicURL}
                className={cx(styles.avatar, { [styles[`avatar-${index}`]]: true })}
                key={name}
                user={societyAvatar}
                size="xsmall"
                tickSize="xsmall"
              />
            );
          })}
        </div>
      )}
      <div className={styles.content}>
        <p>
          {cohosts.length > 0 ? (
            cohosts.map((cohost, index) => {
              const publicURL = PAGE_ROUTES.SOCIETY_PUBLIC.replace('[slug]', cohost.slug);
              return (
                <Link key={index} href={publicURL} underlineOnHover={false} className={styles.name}>
                  {index === 0 ? '' : ', '}
                  {cohost.name}
                </Link>
              );
            })
          ) : (
            <Link href={publicURLForSingleHost} underlineOnHover={false} className={styles.name}>
              {name}
            </Link>
          )}
        </p>
        <span className={styles.separator}></span>
        <DatePosted created_at={posted_at} />
      </div>
    </div>
  );
};

PostedByInfo.displayName = 'PostedByInfo';
